import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Avatar, Spin } from "antd";
import styled from "styled-components";
import { User as UserIcon, Location, Calendar, Cup } from "iconsax-react";
import MobileLayout from "../../components/MobileLayout";
import {
  publicProfileService,
  type PublicProfileData,
} from "../../services/publicProfileService";
import { ACHIEVEMENTS } from "../../config/achievements";
import { fontSizes } from "../../shared/styles/typography";

const Main = styled.main`
  max-width: 600px;
  margin: 0 auto;
  padding: 0 16px 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ProfileHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  padding-top: 8px;

  h2 {
    margin: 0;
    font-size: ${fontSizes.xl}px;
    font-weight: var(--font-weight-bold);
    color: var(--color-text-title);
  }
`;

const BadgesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--color-border) 40%, transparent);
  font-size: ${fontSizes.sm}px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
`;

const StatCard = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 16px 12px;
  text-align: center;

  .value {
    display: block;
    font-size: ${fontSizes.xl}px;
    font-weight: var(--font-weight-bold);
    color: var(--color-primary);
    margin-bottom: 4px;
  }

  .label {
    font-size: ${fontSizes.sm}px;
    color: var(--color-text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 12px;
  font-size: ${fontSizes.xl}px;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-title);
  text-align: center;
`;

const AchievementsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AchievementRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  background: color-mix(in srgb, var(--color-border) 35%, var(--color-surface));
  border: 1px solid var(--color-border);

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${fontSizes.base}px;
    background: var(--color-surface);
    flex-shrink: 0;
  }

  .meta {
    flex: 1;
    min-width: 0;

    h4 {
      margin: 0 0 2px;
      font-size: ${fontSizes.base}px;
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-title);
    }

    p {
      margin: 0;
      font-size: ${fontSizes.sm}px;
      color: var(--color-text-secondary);
    }
  }
`;

const EmptyHint = styled.p`
  text-align: center;
  color: var(--color-text-secondary);
  font-style: italic;
  margin: 0;
`;

const CenterState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 16px;
  color: var(--color-text-secondary);
`;

const GENDER_LABEL: Record<string, string> = {
  masculino: "Masculino",
  feminino: "Feminino",
  "nao-binario": "Não-binário",
  transgenero: "Transgênero",
  "homem-trans": "Homem trans",
  "mulher-trans": "Mulher trans",
  "genero-fluido": "Gênero fluido",
  agenero: "Agênero",
  outro: "Outro",
};

function enrichAchievement(a: {
  id: string;
  name: string;
  icon: string;
  description?: string;
}) {
  const fromConfig = Object.values(ACHIEVEMENTS).find(
    (def) => def.id === a.id || def.name === a.name
  );
  return {
    ...a,
    icon: a.icon || fromConfig?.icon || "🏆",
    description: a.description || fromConfig?.description,
  };
}

const PublicProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<PublicProfileData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    if (!userId) {
      setLoading(false);
      setProfile(null);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    publicProfileService
      .getPublicProfile(userId)
      .then((data) => {
        if (!cancelled) setProfile(data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const location = profile
    ? [profile.city, profile.state, profile.country].filter(Boolean).join(", ")
    : "";

  const achievements = (profile?.achievements || []).map(enrichAchievement);

  let body: React.ReactNode = null;

  if (loading) {
    body = (
      <CenterState>
        <Spin size="large" />
        <span>Carregando perfil…</span>
      </CenterState>
    );
  } else if (!profile) {
    body = (
      <CenterState>
        <Cup size={48} variant="Bold" color="var(--color-primary)" />
        <EmptyHint>Perfil indisponível ou não encontrado.</EmptyHint>
        <button
          type="button"
          onClick={() => navigate("/ranking")}
          style={{
            marginTop: 8,
            background: "var(--color-primary)",
            color: "#fff",
            border: "none",
            borderRadius: 12,
            padding: "10px 18px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Voltar ao ranking
        </button>
      </CenterState>
    );
  } else {
    body = (
      <>
        <ProfileHeader>
          <Avatar
            size={96}
            src={profile.photoURL}
            icon={
              !profile.photoURL ? (
                <UserIcon size={40} variant="Bold" />
              ) : undefined
            }
            style={{
              border: "3px solid var(--color-surface)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            }}
          />
          <h2>{profile.displayName}</h2>
          <BadgesRow>
            {location ? (
              <Badge>
                <Location size={16} variant="Bold" />
                {location}
              </Badge>
            ) : null}
            <Badge>
              <UserIcon size={16} variant="Bold" />
              {profile.isDeaf ? "Surdo" : "Ouvinte"}
            </Badge>
            {profile.age != null && profile.age > 0 ? (
              <Badge>
                <Calendar size={16} variant="Bold" />
                {profile.age} anos
              </Badge>
            ) : null}
            {profile.gender && profile.gender !== "prefiro-nao-informar" ? (
              <Badge>{GENDER_LABEL[profile.gender] || profile.gender}</Badge>
            ) : null}
          </BadgesRow>
        </ProfileHeader>

        <StatsGrid>
          <StatCard>
            <span className="value">{profile.level}</span>
            <span className="label">Nível</span>
          </StatCard>
          <StatCard>
            <span className="value">{profile.points}</span>
            <span className="label">Pontos</span>
          </StatCard>
          <StatCard>
            <span className="value">{profile.streakDays}</span>
            <span className="label">Sequência</span>
          </StatCard>
        </StatsGrid>

        <section>
          <SectionTitle>Conquistas</SectionTitle>
          {achievements.length === 0 ? (
            <EmptyHint>Ainda sem conquistas desbloqueadas.</EmptyHint>
          ) : (
            <AchievementsList>
              {achievements.map((a, index) => (
                <AchievementRow key={a.id || `${a.name}-${index}`}>
                  <div className="icon" aria-hidden>
                    {a.icon}
                  </div>
                  <div className="meta">
                    <h4>{a.name}</h4>
                    {a.description ? <p>{a.description}</p> : null}
                  </div>
                </AchievementRow>
              ))}
            </AchievementsList>
          )}
        </section>
      </>
    );
  }

  return (
    <MobileLayout
      headerTitle={profile?.displayName || "Perfil"}
      headerType="private"
    >
      <Main>{body}</Main>
    </MobileLayout>
  );
};

export default PublicProfile;
